import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, CheckCircle, FileText, Buildings, X, CaretLeft, CaretRight } from '@phosphor-icons/react';

const iconAccent = item => {
  const colors = item.type === 'report' ? ['blue', 'teal', 'amber'] : ['violet', 'coral', 'rose'];
  const hash = [...String(item.key)].reduce((value, char) => ((value * 31) + char.charCodeAt(0)) >>> 0, 0);
  return colors[hash % colors.length];
};

export default function IncubatorDashboardNotifications({ items, pendingApplications, pendingReports, loading, error, onDismiss, onAction }) {
  const viewport = useRef(null);
  const [edges, setEdges] = useState({previous:false,next:false});
  const [dismissing,setDismissing] = useState(null);
  useEffect(()=>{
    const node=viewport.current;
    if(!node) return undefined;
    const measure=()=>setEdges({previous:node.scrollLeft>2,next:node.scrollLeft+node.clientWidth<node.scrollWidth-2});
    measure();
    const observer=new ResizeObserver(measure);observer.observe(node);
    node.addEventListener('scroll',measure,{passive:true});
    return()=>{observer.disconnect();node.removeEventListener('scroll',measure);};
  },[items]);
  const move=direction=>{
    const node=viewport.current,card=node?.querySelector('article');
    if(card)node.scrollBy({left:direction*(card.getBoundingClientRect().width+8),behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
  };
  const dismiss=async (event,item)=>{
    if(dismissing)return;
    const row=event.currentTarget.closest('article');
    const next=(row.nextElementSibling||row.previousElementSibling)?.querySelector('.fb-pulse-tile-action');
    setDismissing(item.key);
    try{await onDismiss(item.key);}finally{
      setDismissing(null);
      requestAnimationFrame(()=>{if(!row.isConnected){if(next?.isConnected)next.focus({preventScroll:true});else viewport.current?.focus({preventScroll:true});}});
    }
  };
  return <section className="inc-notification-center inc-pulse-container" aria-label="Dashboard notifications">
    <div className="fb-pulse-carousel inc-pulse-carousel" aria-label="Recent notification carousel">
      <button type="button" className="fb-pulse-arrow" aria-label="Previous notifications" disabled={!edges.previous} onClick={()=>move(-1)}><CaretLeft size={12} weight="bold"/></button>
      <div className="fb-pulse-tiles" data-count={Math.min(items.length,3)} ref={viewport} role="region" aria-label="Recent notifications" tabIndex={0}
        onKeyDown={event=>{if(event.target===event.currentTarget&&(event.key==='ArrowRight'||event.key==='ArrowLeft')){event.preventDefault();move(event.key==='ArrowRight'?1:-1);}}}
        onFocusCapture={event=>{if(event.target===event.currentTarget||!event.target.matches(':focus-visible'))return;const row=event.target.closest('article'),node=viewport.current;if(row&&node){const bounds=row.getBoundingClientRect(),view=node.getBoundingClientRect();if(bounds.left<view.left)node.scrollLeft-=view.left-bounds.left;else if(bounds.right>view.right)node.scrollLeft+=bounds.right-view.right;}}}>
        {items.length?items.map(item=><article className="fb-pulse-tile" data-unread={!item.read} key={item.key}>
          <div className="fb-pulse-tile-top">
            <span className={`inc-notification-kind is-${iconAccent(item)}`} aria-hidden="true">{item.type==='report'?<FileText size={11}/>:<Buildings size={11}/>}</span>
            <span className="fb-pulse-tile-category">{item.type==='report'?'Monthly update':'Application'}</span>
            {item.created_at&&<span className="fb-pulse-tile-time">{relativeTime(item.created_at)}</span>}
            <button type="button" className="fb-pulse-tile-dismiss" disabled={!!dismissing} aria-label={`Dismiss notification: ${item.title}`} title="Dismiss notification" onClick={event=>dismiss(event,item)}><X size={11} weight="bold"/></button>
          </div>
          <h3 title={item.title}>{item.title}</h3>
          <p title={item.message}>{item.message}</p>
          <button type="button" className="fb-pulse-tile-action" onClick={()=>onAction(item)}>{item.action_label||'Open'}<ArrowRight size={10} weight="bold"/></button>
        </article>):<div className="inc-pulse-empty"><CheckCircle size={22}/><div><strong>{loading?'Loading notifications…':error?'Notifications unavailable':'You’re all caught up'}</strong><span>{loading?'Checking your programs and portfolio.':error?'Refresh the dashboard to try again.':'New application and reporting actions will appear here.'}</span></div></div>}
      </div>
      <button type="button" className="fb-pulse-arrow" aria-label="Next notifications" disabled={!edges.next} onClick={()=>move(1)}><CaretRight size={12} weight="bold"/></button>
    </div>
    {error?<p className="inc-pulse-error" role="alert">{error}</p>:<div className="inc-pulse-totals"><span><b>{pendingApplications??'—'}</b> applications to review</span><i>·</i><span><b>{pendingReports??'—'}</b> updates pending</span></div>}
  </section>;
}

// The startup notification carousel uses the same relative time labels.
const relativeTime=value=>{
  const date=new Date(value);
  if(Number.isNaN(date.getTime()))return 'Recently';
  const seconds=Math.max(0,Math.floor((Date.now()-date.getTime())/1000));
  if(seconds<60)return 'Now';
  if(seconds<3600)return `${Math.floor(seconds/60)}m`;
  if(seconds<86400)return `${Math.floor(seconds/3600)}h`;
  if(seconds<172800)return 'Yesterday';
  if(seconds<604800)return `${Math.floor(seconds/86400)}d`;
  return date.toLocaleDateString(undefined,{month:'short',day:'numeric'});
};
